import React, { useRef, startTransition } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const ProtectedRoute = ({ component: Component, roles, path, children, ...rest }) => {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  
  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole');
  const isAuthenticated = !!userRole;

  // Add debugging logs
  console.log('ProtectedRoute Debug:', {
    userRole,
    isAuthenticated,
    currentPath: location.pathname,
    roles,
    storedToken: localStorage.getItem('auth_token'),
    userObject: JSON.parse(localStorage.getItem('user') || '{}')
  });

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const currentPath = location.pathname;

  // Map localStorage userRole to route roles for compatibility
  const getUserRolesForRouteCheck = (userRole) => {
    switch (userRole) {
      case 'bda':
        return ['bda'];
      case 'service-provider':
      case 'sp_admin':
        return ['sp_admin', 'service-provider'];
      case 'sp_staff':
        return ['sp_staff'];
      case 'admin':
        return ['admin', 'super_admin', 'admin_staff'];
      default:
        return [];
    }
  };

  // Helper function to check if user has the required role
  const hasRequiredRole = () => {
    if (!roles || roles.length === 0) return true;
    
    const userRoles = getUserRolesForRouteCheck(userRole);
    return roles.some(role => userRoles.includes(role));
  };

  // Route guards based on userRole from localStorage - strict enforcement
  const enforceRoleBasedRouting = () => {
    // Prevent redirect loops
    if (previousPathRef.current === currentPath) {
      return null;
    }
    
    previousPathRef.current = currentPath;
    
    switch (userRole) {
      case 'bda':
        // BDA users can only access /bda/* routes
        if (!currentPath.startsWith('/bda/')) {
          return <Navigate to="/bda/dashboard" replace />;
        }
        break;
        
      case 'admin':
        // Admin users can only access admin routes
        if (!currentPath.startsWith('/admin/') && 
            !currentPath.startsWith('/dashboard') && 
            !currentPath.startsWith('/users') && 
            !currentPath.startsWith('/plugs') && 
            !currentPath.startsWith('/notifications') && 
            !currentPath.startsWith('/settings') && 
            !currentPath.startsWith('/profile')) {
          return <Navigate to="/dashboard" replace />;
        }
        break;
        
      case 'service-provider':
      case 'sp_admin':
        // Service provider admin users can only access /sp/* routes (but not staff routes)
        if (!currentPath.startsWith('/sp/')) {
          return <Navigate to="/sp/dashboard" replace />;
        }
        break;
        
      case 'sp_staff':
        // Staff users can only access /sp/staff/* routes
        if (!currentPath.startsWith('/sp/staff/')) {
          return <Navigate to="/sp/staff/dashboard" replace />;
        }
        break;
        
      default:
        // Unknown role, redirect to login
        return <Navigate to="/login" replace />;
    }
    
    return null;
  };

  // Apply strict route enforcement first
  const routeRedirect = enforceRoleBasedRouting();
  if (routeRedirect) {
    return routeRedirect;
  }

  // Check if user has required role for this specific route
  if (!hasRequiredRole()) {
    console.log('ProtectedRoute - User does not have required role:', {
      userRole,
      requiredRoles: roles,
      currentPath: location.pathname,
      userRoles: getUserRolesForRouteCheck(userRole)
    });
    
    // Redirect to appropriate dashboard based on role
    switch (userRole) {
      case 'bda':
        return <Navigate to="/bda/dashboard" replace />;
      case 'admin':
        return <Navigate to="/dashboard" replace />;
      case 'service-provider':
      case 'sp_admin':
        return <Navigate to="/sp/dashboard" replace />;
      case 'sp_staff':
        console.log('ProtectedRoute - Redirecting sp_staff to staff dashboard');
        return <Navigate to="/sp/staff/dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  // Special case: If user is a BDA with revision_needed status, redirect to track status page
  const userStatus = localStorage.getItem('userStatus');
  if (userRole === 'bda' && userStatus === 'revision_needed' && 
      path !== '/bda/track-status' && !currentPath.includes('/bda/track-status')) {
    return <Navigate to="/bda/track-status" replace />;
  }
  
  // Render the protected component
  if (Component) {
    return <Component {...rest}>{children}</Component>;
  }
  
  return children;
};

export default ProtectedRoute;